import React from 'react';
import Chart from "react-apexcharts";
import { PostApiMethod } from "../../core/services/apiService";
import './apexChart.css';
import { dashboardChartProps } from '../../core/dto/dashboard.dto';

interface AgentActivityData {
    agent_id: string;
    agent_name: string;
    online_ms: number;
    busy_ms: number;
    away_ms: number;
    collections_ms: number;
    online_formatted: string;
    busy_formatted: string;
    away_formatted: string;
    collections_formatted: string;
}

const AgentActivityChart = ({ apiEndpoint, startDate, endDate, selectedLocation, selectedAgent, onChartLoaded }: dashboardChartProps) => {
    const [chartDetail, setChartDetail] = React.useState<any>(null);
    const [loaded, setLoaded] = React.useState(false);

    React.useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const payload = {
                    from_date: startDate ? startDate.toLocaleDateString("en-CA") : null,
                    to_date: endDate ? endDate.toLocaleDateString("en-CA") : null,
                    agent_id: selectedAgent ? selectedAgent : "",
                    location_id: selectedLocation ? selectedLocation : "",
                    clientTZ: Intl.DateTimeFormat().resolvedOptions().timeZone
                };

                const response = await PostApiMethod(apiEndpoint, payload);
                const data: AgentActivityData[] = response?.data?.data || [];

                const agentNames = data.map(d => d.agent_name);

                // Convert ms to hours for readable y-axis
                // Apply a minimum display value so tiny non-zero segments are visible in the bar
                const MIN_DISPLAY_HOURS = 0.15; // ~9 min — ensures segment is visible
                const toDisplayHours = (ms: number) => {
                    if (ms === 0) return 0;
                    const hours = parseFloat((ms / 3600000).toFixed(2));
                    return Math.max(hours, MIN_DISPLAY_HOURS);
                };
                const onlineHours = data.map(d => toDisplayHours(d.online_ms));
                const busyHours = data.map(d => toDisplayHours(d.busy_ms));
                const awayHours = data.map(d => toDisplayHours(d.away_ms));
                const collectionsHours = data.map(d => toDisplayHours(d.collections_ms));

                // Store formatted strings for tooltips
                const onlineFormatted = data.map(d => d.online_formatted);
                const busyFormatted = data.map(d => d.busy_formatted);
                const awayFormatted = data.map(d => d.away_formatted);
                const collectionsFormatted = data.map(d => d.collections_formatted);

                const totalCount = data.length;

                const series = [
                    { name: 'Online (hrs)', data: onlineHours },
                    { name: 'Busy (hrs)', data: busyHours },
                    { name: 'Away (hrs)', data: awayHours },
                    { name: 'Collections (hrs)', data: collectionsHours }
                ];

                const options: any = {
                    chart: {
                        type: 'bar',
                        stacked: true,
                        toolbar: { show: true, tools: { download: true } }
                    },
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            columnWidth: agentNames.length <= 3 ? '30%' : '55%',
                            dataLabels: { position: 'center' }
                        }
                    },
                    colors: ['#439a43', '#0187C4', '#ED8B00', '#9C27B0'],
                    xaxis: {
                        categories: agentNames,
                        labels: {
                            trim: false,
                            rotateAlways: agentNames.length > 5,
                            rotate: -45,
                            style: { fontSize: '13px' }
                        }
                    },
                    yaxis: {
                        title: { text: 'Hours', style: { fontSize: '14px', fontWeight: 600 } },
                        labels: {
                            style: { fontSize: '13px' },
                            formatter: (val: number) => val.toFixed(1)
                        }
                    },
                    grid: { padding: { bottom: 20 } },
                    legend: {
                        show: true,
                        position: 'bottom',
                        horizontalAlign: 'center',
                        fontSize: '14px'
                    },
                    dataLabels: {
                        enabled: false
                    },
                    tooltip: {
                        shared: false,
                        intersect: true,
                        custom: function ({ series: s, seriesIndex, dataPointIndex, w }: any) {
                            const agentName = w.globals.labels[dataPointIndex];
                            const onlineTime = onlineFormatted[dataPointIndex] || '0m:0s';
                            const busyTime = busyFormatted[dataPointIndex] || '0m:0s';
                            const awayTime = awayFormatted[dataPointIndex] || '0m:0s';
                            const collectionsTime = collectionsFormatted[dataPointIndex] || '0m:0s';

                            const onlineMs = data[dataPointIndex]?.online_ms || 0;
                            const busyMs = data[dataPointIndex]?.busy_ms || 0;
                            const awayMs = data[dataPointIndex]?.away_ms || 0;
                            const collectionsMs = data[dataPointIndex]?.collections_ms || 0;
                            const totalMs = onlineMs + busyMs + awayMs + collectionsMs;

                            const pct = (ms: number) => totalMs > 0 ? ((ms / totalMs) * 100).toFixed(1) : '0.0';

                            return `<div style="padding: 8px 12px; font-size: 13px;">
                                <strong>${agentName}</strong><br/>
                                <span style="color: #439a43;">&#9679;</span> Online: <strong>${onlineTime}</strong> (${pct(onlineMs)}%)<br/>
                                <span style="color: #0187C4;">&#9679;</span> Busy: <strong>${busyTime}</strong> (${pct(busyMs)}%)<br/>
                                <span style="color: #ED8B00;">&#9679;</span> Away: <strong>${awayTime}</strong> (${pct(awayMs)}%)<br/>
                                <span style="color: #9C27B0;">&#9679;</span> Collections: <strong>${collectionsTime}</strong> (${pct(collectionsMs)}%)
                            </div>`;
                        }
                    }
                };

                if (isMounted) {
                    setChartDetail({ labels: options, series, count: totalCount });
                    if (!loaded) {
                        onChartLoaded?.();
                        setLoaded(true);
                    }
                }
            } catch (err) {
                console.error('Error fetching agent activity reports:', err);
                if (isMounted && !loaded) {
                    onChartLoaded?.();
                    setLoaded(true);
                }
            }
        })();
        return () => { isMounted = false };
    }, [apiEndpoint, startDate, endDate, selectedLocation, selectedAgent]);

    return (
        <div className="overflow-hidden agent-activity-chart-wrapper">
            <div className="row mb-3 chart-header" style={{ borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>
                <div className="col-12"><h5 className="text-center">Agent Status Activity</h5></div>
            </div>
            <div className="col-12">
                {chartDetail && chartDetail.count > 0 ? (
                    <Chart
                        options={chartDetail.labels}
                        series={chartDetail.series}
                        type="bar"
                        height={380}
                    />
                ) : (
                    <div className="text-center py-4">No Records Found</div>
                )}
            </div>
        </div>
    );
};

export default AgentActivityChart;
